import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Calendar, ArrowLeft, Heart, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VefatDetay() {
  const { id } = useParams();
  const [obituary, setObituary] = useState(null);
  const [condolences, setCondolences] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    fetchObituary();
    fetchCondolences();
    checkUser();
  }, [id]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);

  const checkUser = () => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.id) setUser(data);
        })
        .catch(() => {});
    }
  };

  const fetchObituary = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/obituaries`);
      if (response.ok) {
        const data = await response.json();
        const found = data.find(o => o.id === id);
        setObituary(found);
      }
    } catch (error) {
      toast.error('Vefat eden bilgisi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchCondolences = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/obituaries/${id}/condolences`);
      if (response.ok) {
        const data = await response.json();
        setCondolences(data);
      }
    } catch (error) {
      console.error('Taziye mesajları yüklenemedi:', error);
    }
  };

  const handleAddCondolence = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Taziye mesajı yazmak için giriş yapmalısınız');
      return;
    }

    if (!newMessage.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/obituaries/${id}/condolences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: newMessage })
      });

      if (response.ok) {
        toast.success('Taziye mesajınız gönderildi. Admin onayından sonra görünecektir.');
        setNewMessage('');
        fetchCondolences();
      } else {
        toast.error('Mesaj gönderilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-xl text-amber-800">Yükleniyor...</div>
      </div>
    );
  }

  if (!obituary) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-amber-700 mb-4">Kayıt bulunamadı</p>
          <Link to="/vefat-edenler">
            <Button className="bg-amber-600 hover:bg-amber-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Vefat Edenlere Dön
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/vefat-edenler">
          <Button variant="ghost" className="mb-6 text-amber-800 hover:bg-amber-50">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Vefat Edenlere Dön
          </Button>
        </Link>

        <Card className="border-2 border-amber-200 bg-white shadow-xl mb-8">
          <CardContent className="p-8 text-center">
            {obituary.image_url ? (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden border-4 border-amber-300 shadow-lg block cursor-pointer transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-400"
                aria-label="Resmi büyüt"
              >
                <img
                  src={obituary.image_url}
                  alt={`${obituary.first_name} ${obituary.last_name}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ) : (
              <div className="w-48 h-48 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-6xl font-bold border-4 border-amber-300 shadow-lg">
                {obituary.first_name.charAt(0)}{obituary.last_name.charAt(0)}
              </div>
            )}
            
            <h1 className="text-4xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              {obituary.first_name} {obituary.last_name}
            </h1>
            
            <div className="space-y-3 text-amber-700 mb-6">
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>Doğum: {formatDate(obituary.birth_date)}</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>Vefat: {formatDate(obituary.death_date)}</span>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-amber-200">
              <p className="text-2xl text-amber-800 italic">"Mekânı cennet olsun"</p>
              <p className="text-amber-700 mt-4">Ruhu şad olsun</p>
            </div>
          </CardContent>
        </Card>

        {/* Condolence Messages Section */}
        <Card className="border-2 border-amber-200 bg-white">
          <CardHeader>
            <CardTitle className="text-2xl text-amber-900 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif' }}>
              <Heart className="w-6 h-6" />
              Taziye Mesajları ({condolences.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Add Condolence Form */}
            {user ? (
              <form onSubmit={handleAddCondolence} className="mb-8">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Taziye mesajınızı yazın..."
                  rows={4}
                  className="border-amber-300 focus:border-amber-500 mb-3"
                  data-testid="condolence-input"
                />
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                  data-testid="add-condolence-btn"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Taziye Mesajı Gönder
                </Button>
              </form>
            ) : (
              <div className="mb-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800">Taziye mesajı göndermek için <Link to="/uyelik" className="text-orange-600 font-semibold hover:underline">giriş yapın</Link></p>
              </div>
            )}

            {/* Condolences List */}
            <div className="space-y-4">
              {condolences.length === 0 ? (
                <p className="text-center text-amber-600 py-8">Henüz taziye mesajı yok. İlk mesajı siz gönderin!</p>
              ) : (
                condolences.map((message) => (
                  <div 
                    key={message.id} 
                    className="p-4 bg-amber-50 rounded-lg border border-amber-200"
                    data-testid={`condolence-${message.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {message.user_nickname.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-semibold text-amber-900">{message.user_nickname}</p>
                          <p className="text-xs text-amber-600">
                            {new Date(message.created_at).toLocaleDateString('tr-TR')}
                          </p>
                        </div>
                        <p className="text-amber-800">{message.message}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Image Lightbox */}
      {lightboxOpen && obituary.image_url && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 cursor-zoom-out"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white/90 hover:text-white p-2 rounded-full bg-black/40 hover:bg-black/60"
            aria-label="Kapat"
          >
            <X className="w-7 h-7" />
          </button>
          <img
            src={obituary.image_url}
            alt={`${obituary.first_name} ${obituary.last_name}`}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
